import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { usePaymentMethod } from './Billing/usePaymentMethod';
import { getSafePublishableKey } from '../config/stripe';
import { getAccountEmail } from '../utils/currentUser';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../redux';
import { createInvitationPaymentIntent, selectIsCreatingPaymentIntent, selectPaymentIntent, PaymentIntentResponse } from '../redux/billing/billingSlice';

// Initialize Stripe with safe publishable key (never a secret key)
const safePublishableKey = getSafePublishableKey();
const stripePromise = loadStripe(safePublishableKey);

export interface InvitationBillingData {
  inviteCount: number;
  totalAmount: number;
  clinicianNames: string[];
  clinicianIds: string[]; // MongoDB _id for each clinician
  perInviteCost: number;
  priceId: string; // Stripe price ID
  productId: string; // Stripe product ID
  practiceId: string; // Practice ID
  customerId?: string; // Stripe customer ID
  couponCode?: string; // Optional coupon code
  paymentMethodId?: string; // Optional payment method ID
  billingEmail?: string; // Defaults to the signed-in account email
}

export const useStripeInvitation = (userId?: string) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Use Redux slice for payment intent creation
  useSelector(selectIsCreatingPaymentIntent); // Track loading state
  useSelector(selectPaymentIntent); // Track payment intent result
  
  // Use Redux slice via hook instead of Zustand
  const { paymentMethods } = usePaymentMethod({
    userId,
    autoFetch: false,
  });

  const processInvitationPayment = async (billingData: InvitationBillingData) => {
    setIsProcessing(true);
    setError(null);

    try {
      // Get userId from localStorage if not available in hook
      const getUserId = () => {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          return user?.["sub"] || user?.sub || userId || undefined;
        } catch {
          return userId || undefined;
        }
      };
      
      const effectiveUserId = getUserId();
      
      // Get customerId from localStorage if not provided
      const getCustomerId = () => {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          return user?.stripe_customer_id || user?.customerId || billingData.customerId || undefined;
        } catch {
          return billingData.customerId || undefined;
        }
      };
      
      const effectiveCustomerId = getCustomerId();
      
      if (!effectiveUserId) {
        throw new Error('User ID is required');
      }
      
      if (!billingData.priceId) {
        throw new Error('Price ID is required');
      }
      
      if (!billingData.productId) {
        throw new Error('Product ID is required');
      }
      
      if (!billingData.practiceId) {
        throw new Error('Practice ID is required');
      }
      
      // Create payment intent/checkout session on your backend using Redux slice
      const paymentResult = await dispatch(createInvitationPaymentIntent({
        priceId: billingData.priceId,
        productId: billingData.productId,
        userId: effectiveUserId,
        customerId: effectiveCustomerId,
        practiceId: billingData.practiceId,
        clinicianIds: billingData.clinicianIds, // Array of MongoDB _id values
        clinicianNames: billingData.clinicianNames.join(', '), // Comma-separated string
        couponCode: billingData.couponCode,
        paymentMethodId: billingData.paymentMethodId,
        // Rewardful attributes conversions by matching the Stripe customer's
        // email, so the backend must set this on the customer.
        billingEmail: billingData.billingEmail || getAccountEmail(),
      })).unwrap();


      // Extract clientSecret - handle different possible response structures
      const clientSecret = paymentResult?.clientSecret 
        || (paymentResult as { client_secret?: string })?.client_secret 
        || (paymentResult as { data?: { clientSecret?: string; client_secret?: string } })?.data?.clientSecret
        || (paymentResult as { data?: { clientSecret?: string; client_secret?: string } })?.data?.client_secret;

      if (!clientSecret) {
        console.error('❌ Client secret not found in response:', {
          paymentResult,
          keys: Object.keys(paymentResult || {}),
        });
        throw new Error('Client secret not found in payment intent response. Please check the backend response structure.');
      }


      // Store payment data in localStorage BEFORE calling confirmPayment
      // This is necessary because Stripe redirects immediately, so code after confirmPayment won't execute
      // Extract payment intent ID from clientSecret (format: pi_xxx_secret_xxx)
      const paymentIntentIdFromSecret = clientSecret.split('_secret_')[0];
      
      // Extract transactionId and subscriptionId from paymentResult (may not be in typed interface)
      const extendedResult = paymentResult as PaymentIntentResponse & {
        transactionId?: string;
        subscriptionId?: string;
      };
      
      const paymentData = {
        transactionId: extendedResult.transactionId || paymentIntentIdFromSecret,
        subscriptionId: extendedResult.subscriptionId,
        paymentIntentId: paymentIntentIdFromSecret,
      };
      
      localStorage.setItem('pendingPaymentData', JSON.stringify(paymentData));

      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      // Confirm payment with Stripe using the selected payment method.
      // redirect: 'always' — every payment, 3D Secure or not, lands on the
      // success page. That gives one code path for finalization and guarantees
      // affiliate conversions are reported (see useRewardfulConversion); a
      // non-redirecting flow would silently skip tracking for most purchases.
      // On success this call never resolves — the browser navigates away.
      const result = await stripe.confirmPayment({
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/practice/invitation-management/payment/success`,
        },
        redirect: 'always',
      });

      const stripeError = result.error;

      if (stripeError) {
        // Clear localStorage on error
        localStorage.removeItem('pendingPaymentData');
        throw new Error(stripeError.message || 'Payment failed');
      }

      // Unreachable in practice: with redirect: 'always' Stripe either navigates
      // away or resolves with an error. Defensive only.
      throw new Error('Payment was not completed');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment failed';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsProcessing(false);
    }
  };

  const createPaymentIntent = async (billingData: InvitationBillingData) => {
    try {
      // Get userId from localStorage if not available in hook
      const getUserId = () => {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          return user?.["sub"] || user?.sub || userId || undefined;
        } catch {
          return userId || undefined;
        }
      };
      
      const effectiveUserId = getUserId();
      
      // Get customerId from localStorage if not provided
      const getCustomerId = () => {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          return user?.stripe_customer_id || user?.customerId || billingData.customerId || undefined;
        } catch {
          return billingData.customerId || undefined;
        }
      };
      
      const effectiveCustomerId = getCustomerId();
      
      if (!effectiveUserId) {
        throw new Error('User ID is required');
      }
      
      if (!billingData.priceId) {
        throw new Error('Price ID is required');
      }
      
      if (!billingData.productId) {
        throw new Error('Product ID is required');
      }
      
      if (!billingData.practiceId) {
        throw new Error('Practice ID is required');
      }
      
      // Use Redux slice to create payment intent
      const result = await dispatch(createInvitationPaymentIntent({
        priceId: billingData.priceId,
        productId: billingData.productId,
        userId: effectiveUserId,
        customerId: effectiveCustomerId,
        practiceId: billingData.practiceId,
        clinicianIds: billingData.clinicianIds, // Array of MongoDB _id values
        clinicianNames: billingData.clinicianNames.join(', '), // Comma-separated string
        couponCode: billingData.couponCode,
        paymentMethodId: billingData.paymentMethodId,
        billingEmail: billingData.billingEmail || getAccountEmail(),
      })).unwrap();

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create payment intent';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  return {
    processInvitationPayment,
    createPaymentIntent,
    isProcessing,
    error,
    hasPaymentMethods: paymentMethods.length > 0,
  };
};
